import { mapFirestoreTimestamp, type FirestoreApplicationDocData } from '@/lib/applicationFirestoreMapper';
import type { Interview } from '@/types';

type FirestorePipelineRunDoc = Record<string, unknown>;

function mapInterviewTypeLabel(raw: unknown): Interview['type'] {
  switch (raw) {
    case 'phone':
      return 'Phone';
    case 'in_person':
      return 'In-person';
    case 'video_call':
    default:
      return 'Video Call';
  }
}

/** Transcription-only slot — no Google Calendar event (external meet link OK). Matches BE cancelInterview. */
export function isRecordingOnlyInterview(int: {
  calendarEventId?: string;
}): boolean {
  return !int.calendarEventId;
}

/** Mirrors backend `deriveInterviewDisplayStatus` for list-column badges. */
export function deriveInterviewStatusFromRun(
  run: FirestorePipelineRunDoc,
  scheduledIso: string,
  durationMinutes: number,
): Interview['status'] {
  if (run['state'] === 'CANCELLED') return 'Cancelled';
  const startMs = new Date(scheduledIso).getTime();
  const endMs = startMs + durationMinutes * 60 * 1000;
  if (!Number.isNaN(startMs) && endMs < Date.now()) return 'Completed';

  const completedAt = run['completedAt'];
  const updatedAt = run['updatedAt'];
  const completedMs = completedAt ? Date.parse(mapFirestoreTimestamp(completedAt)) : 0;
  const updatedMs = updatedAt ? Date.parse(mapFirestoreTimestamp(updatedAt)) : 0;
  if (completedMs > 0 && updatedMs > completedMs + 2000) return 'Updated';
  return 'Scheduled';
}

/**
 * Maps interview `pipelineRuns` subcollection docs to list-row `interviews[]`.
 * Participant names are omitted — sufficient for `InterviewSubstatusBadges`.
 */
export function mapPipelineRunsToInterviews(
  runs: FirestorePipelineRunDoc[],
  applicationId: string,
  applicationDoc?: FirestoreApplicationDocData,
): Interview[] {
  const collected = (applicationDoc?.['collectedData'] as Record<string, unknown> | undefined) ?? {};
  const meetLink = (collected['meet_link'] as { value?: unknown } | undefined)?.value;
  const calendarLink = (collected['event_html_link'] as { value?: unknown } | undefined)?.value;

  return runs
    .filter((run) => run['pipelineKey'] === 'interview')
    .filter((run) => run['state'] === 'COMPLETED' || run['state'] === 'CANCELLED')
    .map((run) => {
      const inputs = (run['decisionInputs'] as Record<string, unknown> | undefined) ?? {};
      const scheduledAt = mapFirestoreTimestamp(
        inputs['scheduledDate'] ?? run['completedAt'] ?? run['startedAt'] ?? run['createdAt'],
      );
      const durationMinutes =
        typeof inputs['durationMinutes'] === 'number' ? inputs['durationMinutes'] : 60;
      const runId = String(run['id'] ?? '');

      return {
        id: runId,
        pipelineRunId: runId,
        applicationId,
        scheduledAt,
        duration: durationMinutes,
        type: mapInterviewTypeLabel(inputs['interviewType']),
        meetLink: typeof meetLink === 'string' ? meetLink : undefined,
        calendarLink: typeof calendarLink === 'string' ? calendarLink : undefined,
        participants: [],
        notes: typeof inputs['notes'] === 'string' ? inputs['notes'] : undefined,
        status: deriveInterviewStatusFromRun(run, scheduledAt, durationMinutes),
        createdAt:
          (run['createdAt'] ? mapFirestoreTimestamp(run['createdAt']) : undefined)
          ?? (run['startedAt'] ? mapFirestoreTimestamp(run['startedAt']) : undefined)
          ?? (run['completedAt'] ? mapFirestoreTimestamp(run['completedAt']) : undefined),
      } satisfies Interview;
    });
}

export function pickLatestInterviewForList(interviews: Interview[]): Interview | null {
  if (interviews.length === 0) return null;
  return [...interviews].sort((a, b) => {
    const ca = a.createdAt ? Date.parse(a.createdAt) : 0;
    const cb = b.createdAt ? Date.parse(b.createdAt) : 0;
    if (cb !== ca) return cb - ca;
    const sa = Date.parse(a.scheduledAt);
    const sb = Date.parse(b.scheduledAt);
    return (Number.isNaN(sb) ? 0 : sb) - (Number.isNaN(sa) ? 0 : sa);
  })[0] ?? null;
}
